import { X, Minus, Plus, Trash2, Gift } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { createCheckout, formatUsd } from "../lib/api";
import { getStoredUtm } from "../lib/utm";
import { CART } from "../constants/testIds";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, surgeryDate } = useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [gift, setGift] = useState(false);
  const [gp, setGp] = useState({
    purchaser_name: "", recipient_name: "", recipient_email: "",
    line1: "", line2: "", city: "", state: "", postal: "", country: "US",
    message: "",
  });
  const [errors, setErrors] = useState({});

  // Postal code patterns for the countries Stripe accepts on our checkout
  const POSTAL_PATTERNS = {
    US: /^\d{5}(-\d{4})?$/,
    CA: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
    GB: /^[A-Za-z]{1,2}\d[A-Za-z\d]? ?\d[A-Za-z]{2}$/,
    AU: /^\d{4}$/,
  };
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateGift = () => {
    const e = {};
    if (!gp.recipient_name.trim() || gp.recipient_name.trim().length < 2)
      e.recipient_name = "Enter the recipient's full name.";
    if (!EMAIL_RE.test(gp.recipient_email))
      e.recipient_email = "Enter a valid email.";
    if (!gp.line1.trim() || gp.line1.trim().length < 3)
      e.line1 = "Street address is required.";
    if (!gp.city.trim()) e.city = "City is required.";
    if (!gp.state.trim()) e.state = "State/region is required.";
    const pattern = POSTAL_PATTERNS[gp.country] || /^.{3,10}$/;
    if (!pattern.test(gp.postal.trim()))
      e.postal = `Enter a valid ${gp.country} postal code.`;
    return e;
  };

  const composedAddress = () => {
    const parts = [gp.line1, gp.line2, gp.city, `${gp.state} ${gp.postal}`.trim(), gp.country]
      .map((s) => (s || "").trim()).filter(Boolean);
    return parts.join(", ");
  };

  const checkout = async () => {
    if (!items.length) return;
    if (gift) {
      const e = validateGift();
      setErrors(e);
      if (Object.keys(e).length > 0) {
        toast.error("Please fix the highlighted fields.");
        return;
      }
    }
    setLoading(true);
    try {
      const utm = getStoredUtm();
      const payload = {
        items: items.map((i) =>
          i.kind === "refill"
            ? { refill_id: i.id, quantity: i.quantity }
            : { product_id: i.id, quantity: i.quantity, variants: i.variants || {} }
        ),
        origin_url: window.location.origin,
        email: email || undefined,
        surgery_date: surgeryDate || undefined,
        gift,
        gift_recipient_name: gift ? gp.recipient_name : undefined,
        gift_recipient_email: gift ? gp.recipient_email : undefined,
        gift_recipient_address: gift ? composedAddress() : undefined,
        gift_message: gift ? gp.message : undefined,
        purchaser_name: gift ? gp.purchaser_name : undefined,
        utm: utm || undefined,
      };
      const res = await createCheckout(payload);
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error("Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <div
        onClick={() => setOpen(false)}
        className={`fixed inset-0 bg-[#2A1F1F]/40 backdrop-blur-sm z-50 transition-opacity duration-500 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />
      <aside
        data-testid={CART.drawer}
        className={`fixed right-0 top-0 h-full w-full max-w-md bg-[#FBF6F1] z-50 shadow-2xl flex flex-col transition-transform duration-500 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex justify-between items-center px-6 py-5 border-b border-[#F0DFD0]">
          <div>
            <div className="eyebrow">Your Cart</div>
            <h3 className="font-serif text-xl mt-1">Recovery Systems</h3>
          </div>
          <button onClick={() => setOpen(false)} className="p-2 hover:bg-[#FDF3ED]" aria-label="Close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {items.length === 0 ? (
          <div data-testid={CART.empty} className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <div className="w-16 h-16 rounded-full border border-[#E5CDBB] flex items-center justify-center mb-4">
              <span className="font-serif text-xl text-[#A08D8D]">0</span>
            </div>
            <p className="font-serif text-2xl text-[#4A3535]">Your cart is empty</p>
            <p className="text-sm text-[#6B5757] mt-2">Begin by choosing a procedure that matches your journey.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {items.map((i) => (
              <div key={i.key} data-testid={CART.item(i.id)} className="flex gap-4 pb-6 border-b border-[#F0DFD0]">
                <img src={i.image} alt={i.name} className="w-20 h-24 object-cover" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-serif text-base text-[#2A1F1F] leading-tight">{i.name}</h4>
                  <p className="text-xs text-[#6B5757] mt-1">{i.subtitle}</p>
                  {i.swaps && i.swaps.length > 0 && (
                    <div className="mt-2 space-y-0.5">
                      {i.swaps.map((s, si) => (
                        <div key={si} className="text-[10px] font-mono tracking-wide text-[#B85C56] uppercase">
                          ↳ {s.brand} · {s.name}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border border-[#E5CDBB]">
                      <button data-testid={CART.itemQtyMinus(i.id)} onClick={() => updateQty(i.key, -1)} className="p-2 hover:bg-[#FDF3ED]" aria-label="Decrease">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="px-3 text-sm font-mono">{i.quantity}</span>
                      <button data-testid={CART.itemQtyPlus(i.id)} onClick={() => updateQty(i.key, 1)} className="p-2 hover:bg-[#FDF3ED]" aria-label="Increase">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="font-mono text-sm">{formatUsd(i.price * i.quantity)}</span>
                  </div>
                </div>
                <button data-testid={CART.itemRemove(i.id)} onClick={() => removeItem(i.key)} className="p-1 self-start text-[#A08D8D] hover:text-[#2A1F1F]" aria-label="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-[#F0DFD0] px-6 py-6 space-y-4 bg-white">
            {surgeryDate && (
              <div className="text-xs text-[#6B5757] bg-[#FDF3ED] px-3 py-2">
                Delivery targeted before <span className="font-medium text-[#2A1F1F]">{surgeryDate}</span> surgery date.
              </div>
            )}
            <input
              data-testid={CART.emailInput}
              type="email"
              placeholder="Email for order confirmation"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-[#E5CDBB] px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#2A1F1F] rounded-md"
            />

            {/* Gift toggle */}
            <button
              data-testid="cart-gift-toggle"
              onClick={() => setGift((g) => !g)}
              type="button"
              className={`w-full flex items-center justify-between px-4 py-3 border rounded-md text-sm transition-colors ${
                gift ? "border-[#B85C56] bg-[#FDF3ED] text-[#2A1F1F]" : "border-[#E5CDBB] bg-white text-[#6B5757] hover:border-[#2A1F1F]"
              }`}
            >
              <span className="flex items-center gap-2">
                <Gift className="w-4 h-4" /> This is a gift
              </span>
              <span className={`w-9 h-5 rounded-full relative transition-colors ${gift ? "bg-[#B85C56]" : "bg-[#E5CDBB]"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${gift ? "translate-x-4" : "translate-x-0.5"}`} />
              </span>
            </button>

            {gift && (
              <div data-testid="cart-gift-form" className="space-y-2 bg-[#FDF3ED] p-4 rounded-md">
                <input data-testid="cart-gift-purchaser" placeholder="Your name" value={gp.purchaser_name}
                  onChange={(e) => setGp({ ...gp, purchaser_name: e.target.value })}
                  className="w-full text-sm px-3 py-2 bg-white border border-[#E5CDBB] rounded" />

                <input data-testid="cart-gift-recipient-name" placeholder="Recipient's name" value={gp.recipient_name}
                  onChange={(e) => setGp({ ...gp, recipient_name: e.target.value })}
                  className={`w-full text-sm px-3 py-2 bg-white border rounded ${errors.recipient_name ? "border-[#B85C56]" : "border-[#E5CDBB]"}`} required />
                {errors.recipient_name && <div data-testid="cart-gift-err-recipient-name" className="text-[11px] text-[#B85C56]">{errors.recipient_name}</div>}

                <input data-testid="cart-gift-recipient-email" type="email" placeholder="Recipient's email" value={gp.recipient_email}
                  onChange={(e) => setGp({ ...gp, recipient_email: e.target.value })}
                  className={`w-full text-sm px-3 py-2 bg-white border rounded ${errors.recipient_email ? "border-[#B85C56]" : "border-[#E5CDBB]"}`} required />
                {errors.recipient_email && <div data-testid="cart-gift-err-recipient-email" className="text-[11px] text-[#B85C56]">{errors.recipient_email}</div>}

                <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B5757] pt-1">Shipping address</div>

                <input data-testid="cart-gift-line1" placeholder="Street address" value={gp.line1}
                  onChange={(e) => setGp({ ...gp, line1: e.target.value })}
                  className={`w-full text-sm px-3 py-2 bg-white border rounded ${errors.line1 ? "border-[#B85C56]" : "border-[#E5CDBB]"}`} required />
                {errors.line1 && <div data-testid="cart-gift-err-line1" className="text-[11px] text-[#B85C56]">{errors.line1}</div>}

                <input data-testid="cart-gift-line2" placeholder="Apt, suite, floor (optional)" value={gp.line2}
                  onChange={(e) => setGp({ ...gp, line2: e.target.value })}
                  className="w-full text-sm px-3 py-2 bg-white border border-[#E5CDBB] rounded" />

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <input data-testid="cart-gift-city" placeholder="City" value={gp.city}
                      onChange={(e) => setGp({ ...gp, city: e.target.value })}
                      className={`w-full text-sm px-3 py-2 bg-white border rounded ${errors.city ? "border-[#B85C56]" : "border-[#E5CDBB]"}`} required />
                    {errors.city && <div data-testid="cart-gift-err-city" className="text-[11px] text-[#B85C56] mt-1">{errors.city}</div>}
                  </div>
                  <div>
                    <input data-testid="cart-gift-state" placeholder="State / Region" value={gp.state}
                      onChange={(e) => setGp({ ...gp, state: e.target.value })}
                      className={`w-full text-sm px-3 py-2 bg-white border rounded ${errors.state ? "border-[#B85C56]" : "border-[#E5CDBB]"}`} required />
                    {errors.state && <div data-testid="cart-gift-err-state" className="text-[11px] text-[#B85C56] mt-1">{errors.state}</div>}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <input data-testid="cart-gift-postal" placeholder="ZIP / Postal code" value={gp.postal}
                      onChange={(e) => setGp({ ...gp, postal: e.target.value })}
                      className={`w-full text-sm px-3 py-2 bg-white border rounded ${errors.postal ? "border-[#B85C56]" : "border-[#E5CDBB]"}`} required />
                    {errors.postal && <div data-testid="cart-gift-err-postal" className="text-[11px] text-[#B85C56] mt-1">{errors.postal}</div>}
                  </div>
                  <div>
                    <select data-testid="cart-gift-country" value={gp.country}
                      onChange={(e) => setGp({ ...gp, country: e.target.value })}
                      className="w-full text-sm px-3 py-2 bg-white border border-[#E5CDBB] rounded">
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                      <option value="GB">United Kingdom</option>
                      <option value="AU">Australia</option>
                    </select>
                  </div>
                </div>

                <textarea data-testid="cart-gift-message" placeholder="A short personal note (optional)" rows={2}
                  value={gp.message} onChange={(e) => setGp({ ...gp, message: e.target.value })}
                  className="w-full text-sm px-3 py-2 bg-white border border-[#E5CDBB] rounded" />
                <div className="text-[10px] text-[#6B5757] font-mono uppercase tracking-widest">
                  We'll ship to your recipient and email them your note once the order confirms.
                </div>
              </div>
            )}
            <div className="flex justify-between items-baseline">
              <span className="eyebrow">Subtotal</span>
              <span data-testid={CART.subtotal} className="font-serif text-2xl">{formatUsd(subtotal)}</span>
            </div>
            <button
              data-testid={CART.checkoutBtn}
              onClick={checkout}
              disabled={loading}
              className="btn-primary w-full disabled:opacity-60"
            >
              {loading ? "Redirecting…" : "Checkout with Stripe"}
            </button>
            <p className="text-[10px] text-center text-[#A08D8D] font-mono tracking-wider uppercase">Secure Payment via Stripe</p>
          </div>
        )}
      </aside>
    </>
  );
}
